import { defineStore } from 'pinia';
import { useNuxtApp } from '#app';
import axios from "axios";
import type {
    Purpose,
    TransportationMethod,
    DocumentType,
    AmountInsured,
    ApiAmountInsured,
    ApiDocumentType, ApiTransportationMethod, ApiPurpose, TravelForm, TravelInsuranceCompany, ApiTravelDetail
} from "~/types/Travel";
import type {RcaInsuranceCompany} from "~/types/Rca";

export const useTravelStore = defineStore('travel', {
    state: () => ({
        purposeList: [] as Purpose[],
        _fetchPurposeListPromise: null as Promise<any> | null,
        transportationMethodList: [] as TransportationMethod[],
        _fetchTransportationMethodListPromise: null as Promise<any> | null,
        documentTypeList: [] as DocumentType[],
        _fetchDocumentTypeListPromise: null as Promise<any> | null,
        amountInsuredList: [] as AmountInsured[],
        _fetchAmountInsuredListPromise: null as Promise<any> | null,
        travelDetail: [],
        _storeTravelDetailPromise: null as Promise<any> | null,
        insuranceCompanies: [] as TravelInsuranceCompany[],
        _fetchInsuranceCompaniesPromise: null as Promise<any> | null,
        amountKeyList: {
            amount10k: 0,
            amount30k: 1,
            amount50k: 2,
        },
        _sendOffersToUserPromise: null as Promise<any> | null,
    }),
    actions: {
        async fetchPurposeList() {
            if (this._fetchPurposeListPromise) return this._fetchPurposeListPromise;

            this._fetchPurposeListPromise = (async () => {
                try {
                    const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/travel-details/purpose-list`);
                    if (response.data.data) {
                        this.purposeList = response.data.data.map((purpose: ApiPurpose) => ({
                            id: purpose.id,
                            code: purpose.code,
                            name: purpose.value,
                        }));
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this._fetchPurposeListPromise = null;
                }
            })();

            return this._fetchPurposeListPromise;
        },
        async getPurposeList(): Promise<Purpose[]> {
            if (this.purposeList.length === 0) {
                await this.fetchPurposeList();
            }
            return this.purposeList;
        },
        async fetchTransportationMethodList() {
            if (this._fetchTransportationMethodListPromise) return this._fetchTransportationMethodListPromise;

            this._fetchTransportationMethodListPromise = (async () => {
                try {
                    const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/travel-details/transportation-method-list`);
                    if (response.data.data) {
                        this.transportationMethodList = response.data.data.map((transportationMethod: ApiTransportationMethod) => ({
                            id: transportationMethod.id,
                            code: transportationMethod.code,
                            name: transportationMethod.value,
                        }));
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this._fetchTransportationMethodListPromise = null;
                }
            })();

            return this._fetchTransportationMethodListPromise;
        },
        async getTransportationMethodList() {
            if (this.transportationMethodList.length === 0) {
                await this.fetchTransportationMethodList();
            }
            return this.transportationMethodList;
        },
        async fetchDocumentTypeList() {
            if (this._fetchDocumentTypeListPromise) return this._fetchDocumentTypeListPromise;

            this._fetchDocumentTypeListPromise = (async () => {
                try {
                    const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/travel-details/document-type-list`);
                    if (response.data.data) {
                        this.documentTypeList = response.data.data.map((documentType: ApiDocumentType) => ({
                            id: documentType.id,
                            code: documentType.code,
                            name: documentType.value,
                        }));
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this._fetchDocumentTypeListPromise = null;
                }
            })();

            return this._fetchDocumentTypeListPromise;
        },
        async getDocumentTypeList() {
            if (this.documentTypeList.length === 0) {
                await this.fetchDocumentTypeList();
            }
            return this.documentTypeList;
        },
        async fetchAmountInsuredList() {
            if (this._fetchAmountInsuredListPromise) return this._fetchAmountInsuredListPromise;

            this._fetchAmountInsuredListPromise = (async () => {
                try {
                    const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/travel-details/amount-insured-list`);
                    if (response.data.data) {
                        this.amountInsuredList = response.data.data.map((amountInsured: ApiAmountInsured) => ({
                            id: amountInsured.id,
                            name: amountInsured.value,
                        }));
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this._fetchAmountInsuredListPromise = null;
                }
            })();

            return this._fetchAmountInsuredListPromise;
        },
        async getAmountInsuredList() {
            if (this.amountInsuredList.length === 0) {
                await this.fetchAmountInsuredList();
            }
            return this.amountInsuredList
        },
        async storeTravelDetail(travelForm: TravelForm, accessToken: string, token: string|undefined|null): Promise<ApiTravelDetail|undefined> {
            if (this._storeTravelDetailPromise) return this._storeTravelDetailPromise;
            if (token) {
                travelForm.token = token;
            }
            this._storeTravelDetailPromise = (async () => {
                try {
                    const response = await axios.post(`${import.meta.env.VITE_API_BASE_URL}/api/v1/travel-details`, travelForm, {
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                        },
                    });
                    if (response.data.data) {
                        return response.data.data as ApiTravelDetail;
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this._storeTravelDetailPromise = null;
                }
            })();

            return this._storeTravelDetailPromise;
        },
        async fetchInsuranceCompanies() {
            if (this._fetchInsuranceCompaniesPromise) return this._fetchInsuranceCompaniesPromise;

            this._fetchInsuranceCompaniesPromise = (async () => {
                try {
                    const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/travel-insurance-companies`);
                    if (response.data.data) {
                        this.insuranceCompanies = response.data.data;
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this._fetchInsuranceCompaniesPromise = null;
                }
            })();

            return this._fetchInsuranceCompaniesPromise;
        },
        async getInsuranceCompanies(): Promise<TravelInsuranceCompany[]> {
            if (this.insuranceCompanies.length === 0) {
                await this.fetchInsuranceCompanies();
            }
            return this.insuranceCompanies;
        },
        async fetchOffers(travelDetailId: number, insuranceCompanyId: number, amountCoveredId: number, accessToken: string, token: string|undefined|null) {
            const formData = {
                travel_detail_id: travelDetailId,
                travel_insurance_company_id: insuranceCompanyId,
                amount_covered_id: amountCoveredId,
                token: '',
            }
            if (token) {
                formData.token = token;
            }
            try {
                const response = await axios.post(`${import.meta.env.VITE_API_BASE_URL}/api/v1/travel-offers`, formData, {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                });
                return response.data.data;
            } catch (error) {
                throw error;
            }
        },
        async sendOffersToUser(offers: { [key: string]: any },
                               insuranceCompanies: TravelInsuranceCompany[],
                               email: string,
                               accessToken: string|null,
                               token: string|undefined) {
            if (this._sendOffersToUserPromise) return this._sendOffersToUserPromise;

            this._sendOffersToUserPromise = (async () => {
                try {
                    const config = accessToken ? {
                        headers: {
                            Authorization: `Bearer ${accessToken}`
                        }
                    } : {};

                    const response = await axios.post(`${import.meta.env.VITE_API_BASE_URL}/api/v1/travel-insurance-offers/send`, {
                        travel_offers_data: offers,
                        email,
                        insurance_companies: insuranceCompanies,
                        token,
                    }, config);
                    if (response.data.data) {
                        return response.data.data;
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this._sendOffersToUserPromise = null;
                }
            })();

            return this._sendOffersToUserPromise;
        },
    },
    persist: {
        storage: persistedState.sessionStorage,
        paths: [
            'purposeList',
            'transportationMethodList',
            'documentTypeList',
            'amountInsuredList',
            'insuranceCompanies',
        ],
        key: 'travelStore',
    }
});
